// src/api/api.js
import axios from 'axios';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// ============================================================
// 1. INTERCEPTEUR DE REQUÊTE (Envoi du token)
// ============================================================
api.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    error => Promise.reject(error)
);

// ============================================================
// 2. INTERCEPTEUR DE RÉPONSE (Gestion de l'expiration)
// ============================================================
api.interceptors.response.use(
    // Si tout va bien (status 2xx), on laisse passer
    response => {
        return response;
    },
    // En cas d'erreur
    error => {
        // On vérifie si c'est une erreur 401 (Non autorisé) ou 403 (Interdit)
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            
            // On évite de faire ça si on est DÉJÀ sur la page de login (pour éviter une boucle infinie lors d'une erreur de mot de passe par exemple)
            if (window.location.pathname !== '/login') {
                console.warn("Session expirée ou invalide. Redirection vers le login...");

                // 1. Nettoyage du stockage local
                localStorage.removeItem('token');
                // localStorage.removeItem('user'); // Décommente si tu stockes des infos user

                // 2. Redirection brutale (force le rechargement et vide les states React)
                window.location.href = '/login';
            }
        }

        // On rejette l'erreur pour que le composant qui a appelé l'API sache qu'il y a eu un problème (ex: pour arrêter un spinner de chargement)
        return Promise.reject(error);
    }
);

export const getFinancialStats = async (resourceId) => {
    return api.get(`/financials/all-payments/${resourceId}`);
};

export const updateAjustement = async (data) => {
    // data: { resource_id, period, amount, motif }
    return api.post('/financials/update-ajustement', data);
};

export const updateGestionRate = async (data) => {
    return api.post('/financials/update-gestion', data);
};

export const updateVirement = async (data) => {
    // data: { resource_id, period, amount }
    return api.post('/financials/update-virement', data);
};

export const getActiveResourceInfo = async (resourceId) => {
    return api.get(`/resource/active-info/${resourceId}`);
};

// === NOUVEAU : Alertes Missions ===
export const getMissionAlerts = async (resourceId) => {
    return api.get(`/resource/mission-alerts/${resourceId}`);
};

// Remplace getAdminStats par ceci :
export const getAdminStats = async (manager = '') => {
    const params = new URLSearchParams();
    if (manager) params.append('manager', manager);
    return api.get(`/admin/stats?${params.toString()}`);
};

// Remplace getSuperAdminStats par ceci :
export const getSuperAdminStats = async (year, manager = '') => {
    const params = new URLSearchParams({ year });
    if (manager) params.append('manager', manager);
    return api.get(`/superadmin/financial-stats?${params.toString()}`);
};

// ============================================
//  SUPER ADMIN - CHARGES INTERCONTRAT (DÉTAIL)
// ============================================
export const getIntercontratDetails = async (year, manager = '') => {
    const params = new URLSearchParams({ year });
    if (manager) params.append('manager', manager);
    return api.get(`/superadmin/intercontrat-details?${params.toString()}`);
};

// ============================================
//  SUPER ADMIN - IMPAYÉS (AVEC FILTRES)
// ============================================
export const getUnpaidDetails = async (year, manager = '') => {
    const params = new URLSearchParams({ year });
    if (manager) params.append('manager', manager);
    return api.get(`/superadmin/unpaid-details?${params.toString()}`);
};

export const getAdminClients = (page = 1, limit = 10, search = '', filters = {}) => {
    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
    });
    
    if (search) params.append('search', search);
    if (filters.hasEmail) params.append('hasEmail', filters.hasEmail);
    if (filters.hasSiret) params.append('hasSiret', filters.hasSiret);
    
    return api.get(`/admin/clients?${params.toString()}`);
};

// ============================================
//  ADMIN - RESSOURCES (AVEC FILTRES)
// ============================================
export const getAdminResources = (page = 1, limit = 10, search = '', filters = {}) => {
    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
    });
    
    if (search) params.append('search', search);
    if (filters.status) params.append('status', filters.status);
    if (filters.country) params.append('country', filters.country);
    
    return api.get(`/admin/resources?${params.toString()}`);
};

// ============================================
//  ADMIN - MISSIONS (AVEC FILTRES)
// ============================================
export const getAdminMissions = (page = 1, limit = 10, search = '', filters = {}) => {
    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
    });
    
    if (search) params.append('search', search);
    if (filters.status) params.append('status', filters.status);
    if (filters.tjmMin) params.append('tjmMin', filters.tjmMin);
    if (filters.tjmMax) params.append('tjmMax', filters.tjmMax);
    if (filters.dateDebut) params.append('dateDebut', filters.dateDebut);
    if (filters.dateFin) params.append('dateFin', filters.dateFin);
    
    return api.get(`/admin/missions?${params.toString()}`);
};

export const getClientDetails = async (clientId) => {
    return api.get(`/admin/clients/${clientId}/details`);
};

export const getResourceDetails = async (resourceId) => {
    return api.get(`/admin/resources/${resourceId}/details`);
};

export const getMissionDetails = async (prestationId) => {
    return api.get(`/admin/missions/${prestationId}/details`);
};

export const changePassword = async (data) => {
    // data: { oldPassword, newPassword }
    return api.post('/auth/change-password', data);
};

export const sendSupportMessage = (data) => {
    return api.post('/support/contact', data);
};

export const getAdminTickets = () => {
    return api.get('/admin/tickets');
};

export const updateTicketStatus = (ticketId, newStatus) => {
    return api.put(`/admin/tickets/${ticketId}/status`, { status: newStatus });
};

export const getPaidHistory = async (resourceId) => {
    return api.get(`/financials/paid-history/${resourceId}`);
};

export const submitOnboarding = async (data) => {
    // data: { current_tjm, final_client, current_poste, esn_name }
    return api.post('/resource/onboarding', data);
};

export const getGestionHistory = async (resourceId) => {
    return api.get(`/financials/gestion-history/${resourceId}`);
};

export const updateMissionSpecificRate = async (data) => {
    // data: { prestation_id: 123, taux_specifique: 8.5 }
    return api.put('/admin/missions/update-rate', data);
};

// Récupérer le détail des notes de frais d'un mois spécifique
export const getMonthExpenses = (resourceId, year, month) => {
    return api.get(`/admin/expenses/${resourceId}/${year}/${month}`);
};

// Mettre à jour la déduction manuelle sur une note de frais spécifique
export const updateExpenseDeduction = (expenseId, deductionValue) => {
    return api.put(`/admin/expenses/${expenseId}/deduction`, {
        deduction: deductionValue
    });
};

export default api;